import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import HeroSection from '../components/common/HeroSection';
import ServiceSidebar from '../components/services/ServiceSidebar';
import services from '../data/services';

const ServiceDetail = () => {
  const { serviceId, subServiceId } = useParams();
  const [service, setService] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 250,
      behavior: 'smooth',
    });
  }, [location.pathname]);

  useEffect(() => {
    const foundService = services.find((s) => s.id === serviceId);

    if (!foundService) {
      navigate('/services');
      return;
    }

    if (subServiceId) {
      const foundSubService = foundService.subServices?.find(
        (sub) => sub.id === subServiceId
      );
      if (!foundSubService) {
        navigate('/services');
        return;
      }

      setService({
        ...foundService,
        title: foundSubService.title,
        fullDescription: foundSubService.fullDescription,
        image: foundSubService.image || foundService.image,
      });
    } else {
      setService(foundService);
    }
  }, [serviceId, subServiceId, navigate]);

  if (!service) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="service-detail-page">
      <HeroSection
        title={service.title}
        breadcrumb={[
          { name: 'Services', url: '/services' },
          { name: service.title },
        ]}
        background="/assets/images/services/services-banner.png"
      />

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar */}
            <div className="lg:w-1/4">
              <div className="sticky top-1">
                <ServiceSidebar currentServiceId={serviceId} />
              </div>
            </div>

            {/* Content */}
            <div className="lg:w-3/4">
              {subServiceId && (
                <Link
                  to={`/services/${serviceId}`}
                  className="text-blue-600 underline mb-6 inline-block"
                >
                  ← Back to {services.find((s) => s.id === serviceId)?.title}
                </Link>
              )}

              <img
                src={service.image}
                alt={service.title}
                className="w-full h-64 object-cover rounded-lg shadow-md mb-8"
              />

              <h2 className="text-3xl font-bold text-gray-800 mb-6">
                {service.title}
              </h2>

              <div
                className="prose max-w-none text-gray-600 mb-8"
                dangerouslySetInnerHTML={{ __html: service.fullDescription }}
              />

              {service.highlights && (
                <div className="bg-gray-50 p-6 rounded-lg mb-8">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Key Features</h3>
                  <ul className="space-y-2">
                    {service.highlights.map((highlight, index) => (
                      <li key={index} className="flex items-start">
                        <div className="mr-3 mt-1 text-blue-600">
                          <svg
                            className="w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {service.subServices && !subServiceId && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Related Services</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {service.subServices.map((subService) => (
                      <Link
                        key={subService.id}
                        to={`/services/${service.id}/${subService.id}`}
                        className="p-4 border border-gray-200 rounded-lg hover:bg-blue-50 transition-colors"
                      >
                        <h4 className="font-bold text-gray-800 mb-1">
                          {subService.title}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {subService.description}
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              <div className="text-center mt-12">
                <Link
                  to="/contact-us"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg inline-block transition-colors"
                >
                  {service.getInTouch}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
